import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle, Clock, Loader2, User } from 'lucide-react';
import { ApiService } from '../services/api';
import { Task } from '../types';
import { useToast } from '../context/ToastContext';

type SourceFilter = 'all' | 'workflow' | 'compliance';
type SeverityFilter = 'all' | 'low' | 'medium' | 'high' | 'critical';
type StatusFilter = 'all' | 'open' | 'resolved';

const Tasks: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [resolvingTaskId, setResolvingTaskId] = useState<string | null>(null);
  const [assigningTaskId, setAssigningTaskId] = useState<string | null>(null);

  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');

  useEffect(() => {
    void loadTasks();
  }, []);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const data = await ApiService.getTasks();
      setTasks(data || []);
    } finally {
      setLoading(false);
    }
  };

  const assigneeOptions = useMemo(() => {
    const values = new Set<string>();
    tasks.forEach((task) => {
      if (task.assignee_user_id) values.add(task.assignee_user_id);
    });
    return Array.from(values);
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    return tasks.filter((task) => {
      const source = task.source || 'workflow';
      const severity = task.severity || 'medium';
      if (sourceFilter !== 'all' && source !== sourceFilter) return false;
      if (severityFilter !== 'all' && severity !== severityFilter) return false;
      if (statusFilter !== 'all' && task.status !== statusFilter) return false;
      if (assigneeFilter === 'unassigned' && !!task.assignee_user_id) return false;
      if (assigneeFilter !== 'all' && assigneeFilter !== 'unassigned' && task.assignee_user_id !== assigneeFilter) return false;
      return true;
    });
  }, [assigneeFilter, severityFilter, sourceFilter, statusFilter, tasks]);

  const handleResolve = async (task: Task, e: React.MouseEvent) => {
    e.stopPropagation();
    setResolvingTaskId(task.id);
    try {
      await ApiService.resolveComplianceTask(task.id, 'Resolved from compliance inbox');
      addToast('Task marked as resolved', 'success');
      await loadTasks();
    } finally {
      setResolvingTaskId(null);
    }
  };

  const handleAssign = async (taskId: string, userId: string | null, e: React.ChangeEvent<HTMLSelectElement>) => {
    e.stopPropagation();
    setAssigningTaskId(taskId);
    try {
      await ApiService.assignComplianceTask(taskId, userId);
      addToast('Task assignment updated', 'success');
      await loadTasks();
    } finally {
      setAssigningTaskId(null);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'text-red-700 bg-red-50 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-900';
      case 'high':
        return 'text-amber-700 bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-900';
      case 'low':
        return 'text-blue-700 bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-900';
      default:
        return 'text-slate-700 bg-slate-50 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
    }
  };

  const routeForTask = (task: Task) => {
    const entityType = String(task.metadata?.related_entity_type || '').toLowerCase();
    if (entityType === 'ticket' && task.related_id) return `/tickets/${task.related_id}`;
    if (entityType === 'vehicle') return '/vehicles';
    if (task.source === 'defect') return null;
    if (task.related_id) return `/tickets/${task.related_id}`;
    return null;
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Compliance Inbox</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Track workflow and compliance breaches in one queue.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4">
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value as SourceFilter)}
          className="px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
        >
          <option value="all">All Sources</option>
          <option value="workflow">Workflow</option>
          <option value="compliance">Compliance</option>
        </select>
        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value as SeverityFilter)}
          className="px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
        >
          <option value="all">All Severity</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
        >
          <option value="all">All Status</option>
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
        </select>
        <select
          value={assigneeFilter}
          onChange={(e) => setAssigneeFilter(e.target.value)}
          className="px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
        >
          <option value="all">All Assignees</option>
          <option value="unassigned">Unassigned</option>
          {assigneeOptions.map((assigneeId) => (
            <option key={assigneeId} value={assigneeId}>
              {assigneeId.slice(0, 8)}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => void loadTasks()}
          className="px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
        >
          Refresh
        </button>
      </div>

      <div className="space-y-3">
        {filteredTasks.length > 0 ? (
          filteredTasks.map((task) => {
            const source = task.source || 'workflow';
            const severity = task.severity || 'medium';
            const taskRoute = routeForTask(task);
            const isOpen = task.status === 'open';

            return (
              <div
                key={task.id}
                onClick={() => (taskRoute ? navigate(taskRoute) : null)}
                className={`bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm transition-shadow ${taskRoute ? 'hover:shadow-md cursor-pointer' : ''}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex gap-3 min-w-0">
                    <div className={`p-2.5 rounded-full h-fit ${severity === 'critical' || severity === 'high' ? 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400' : 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'}`}>
                      <AlertTriangle size={18} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-semibold text-slate-800 dark:text-white">{task.title}</h3>
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded border uppercase ${getSeverityColor(severity)}`}>
                          {severity}
                        </span>
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded border bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700 uppercase">
                          {source}
                        </span>
                        {!isOpen && (
                          <span className="text-[11px] font-semibold px-2 py-0.5 rounded border bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800">
                            Resolved
                          </span>
                        )}
                      </div>
                      <p className="text-slate-600 dark:text-slate-400 text-sm">{task.description}</p>
                      <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-slate-400 dark:text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          Created {new Date(task.created_at).toLocaleDateString('en-GB')}
                        </span>
                        {task.due_date && (
                          <span>Due {new Date(task.due_date).toLocaleDateString('en-GB')}</span>
                        )}
                        {task.breach_code && (
                          <span className="font-mono text-[11px]">{task.breach_code}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <div className="relative">
                      <User size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
                      <select
                        value={task.assignee_user_id || ''}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => void handleAssign(task.id, e.target.value || null, e)}
                        disabled={assigningTaskId === task.id}
                        className="pl-7 pr-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg text-xs bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300"
                      >
                        <option value="">Unassigned</option>
                        {assigneeOptions.map((assigneeId) => (
                          <option key={assigneeId} value={assigneeId}>
                            {assigneeId.slice(0, 8)}
                          </option>
                        ))}
                      </select>
                    </div>
                    {isOpen && (
                      <button
                        onClick={(e) => void handleResolve(task, e)}
                        disabled={resolvingTaskId === task.id}
                        className="p-2 text-slate-300 dark:text-slate-600 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-full transition-all"
                        title="Mark as resolved"
                      >
                        {resolvingTaskId === task.id ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-12 bg-white dark:bg-slate-900 rounded-xl border border-dashed border-slate-300 dark:border-slate-700">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h3 className="text-lg font-bold text-slate-800 dark:text-white">No tasks found</h3>
            <p className="text-slate-500 dark:text-slate-400">Try adjusting your filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tasks;

